import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatCurrency, formatPercent } from '@/lib/utils'
import {
  Search,
  PieChart,
  Activity,
  FileText,
  DollarSign,
  BarChart3,
  TrendingUp,
  Zap,
  Trophy,
  FolderOpen,
  UserCheck,
  Shield,
  Database,
} from 'lucide-react'

const quickLinks = [
  {
    name: 'Fund Screening',
    description: 'Search and filter hedge funds',
    href: '/screening',
    icon: Search,
  },
  {
    name: "What's Working",
    description: 'Top & bottom YTD performers',
    href: '/whats-working',
    icon: Trophy,
  },
  {
    name: 'Portfolio Analysis',
    description: 'Analyze your current portfolio',
    href: '/portfolio',
    icon: PieChart,
  },
  {
    name: 'Portfolio Simulation',
    description: 'Backtest portfolio allocations',
    href: '/portfolio/simulation',
    icon: TrendingUp,
  },
  {
    name: 'Demo Portfolios',
    description: 'Browse and build demo portfolios',
    href: '/portfolio/demo',
    icon: FolderOpen,
  },
  {
    name: 'Risk Dashboard',
    description: 'Monitor portfolio risk metrics',
    href: '/risk',
    icon: Activity,
  },
  {
    name: 'High-Freq Returns',
    description: 'Intra-month return snapshots',
    href: '/high-freq',
    icon: Zap,
  },
  {
    name: 'OPERA Reports',
    description: 'View fund operational data',
    href: '/opera',
    icon: FileText,
  },
  {
    name: 'AUM Monitoring',
    description: 'Track fund flows and AUM',
    href: '/aum',
    icon: DollarSign,
  },
  {
    name: 'Personnel Monitoring',
    description: 'Key personnel data feed',
    href: '/personnel',
    icon: UserCheck,
  },
  {
    name: 'Factor Modeling',
    description: 'Factor exposures & correlations',
    href: '/factors',
    icon: BarChart3,
  },
  {
    name: 'Data Validation',
    description: 'Validate data integrity',
    href: '/data/validation',
    icon: Database,
  },
  {
    name: 'Insert Returns',
    description: 'Enter fund return data',
    href: '/data/returns',
    icon: Database,
  },
  {
    name: 'Doc Completeness',
    description: 'Trigger Jenkins doc checks',
    href: '/data/docs',
    icon: Shield,
  },
]

export default function Dashboard() {
  const { data: portfolio, isLoading: portfolioLoading } = useQuery({
    queryKey: ['portfolio'],
    queryFn: () => api.get('/portfolio').then((res) => res.data),
  })

  const { data: riskData, isLoading: riskLoading } = useQuery({
    queryKey: ['risk-dashboard'],
    queryFn: () => api.get('/risk/dashboard').then((res) => res.data),
  })
  const pf = riskData?.portfolio

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Welcome to Plutus - Hedge Fund Analytics Platform</p>
      </div>

      {/* Portfolio risk & diversification KPIs (36-month lookback) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Kpi label="Annualized Return" loading={riskLoading}
          value={pf?.ann_return != null ? formatPercent(pf.ann_return) : '-'} />
        <Kpi label="Annualized Volatility" loading={riskLoading}
          value={pf?.ann_volatility != null ? formatPercent(pf.ann_volatility) : '-'} />
        <Kpi label="Max Drawdown" loading={riskLoading}
          value={pf?.max_drawdown != null ? formatPercent(pf.max_drawdown) : '-'} />
        <Kpi label="Avg Pairwise Correlation" loading={riskLoading}
          value={pf?.avg_pairwise_correlation != null ? pf.avg_pairwise_correlation.toFixed(2) : '-'} />
        <Kpi label="Downside Correlation" loading={riskLoading}
          value={pf?.avg_downside_correlation != null ? pf.avg_downside_correlation.toFixed(2) : '-'} />
      </div>

      {/* Portfolio facts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Kpi label="Portfolio Value" loading={portfolioLoading} value={formatCurrency(portfolio?.total_value)} />
        <Kpi label="Holdings" loading={portfolioLoading} value={String(portfolio?.holdings?.length || 0)} />
      </div>

      {/* Quick links */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Quick Access</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="bg-card border border-border rounded-lg p-4 hover:border-primary/30 hover:shadow-md transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                  <link.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="font-medium group-hover:text-primary transition-colors">
                    {link.name}
                  </div>
                  <div className="text-sm text-muted-foreground">{link.description}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent holdings */}
      {portfolio?.holdings?.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Portfolio Holdings</h2>
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Fund</th>
                  <th className="text-right">Value</th>
                  <th className="text-right">Weight</th>
                </tr>
              </thead>
              <tbody>
                {portfolio.holdings.slice(0, 5).map((holding: any) => (
                  <tr key={holding.fund_id}>
                    <td className="font-medium">{holding.fund_name}</td>
                    <td className="text-right">{formatCurrency(holding.value)}</td>
                    <td className="text-right">{formatPercent(holding.weight)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {portfolio.holdings.length > 5 && (
              <div className="p-3 border-t border-border text-center">
                <Link to="/portfolio" className="text-sm text-primary hover:underline">
                  View all {portfolio.holdings.length} holdings
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Kpi({ label, value, loading }: { label: string; value: string; loading?: boolean }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="text-sm text-muted-foreground mb-1">{label}</div>
      <div className="text-2xl font-bold tabular-nums">
        {loading ? <span className="spinner h-6 w-6" /> : value}
      </div>
    </div>
  )
}
